import * as React from 'react'
import styled from 'styled-components'
import { useImmer } from 'use-immer'
import { Button, Tooltip, Spin, Empty } from 'antd'
import { isEmpty, isNil, uniqBy, orderBy, set } from 'lodash'
import { t } from 'helpers/i18n'
import { showError, showClientNotifications } from 'helpers/errors'
import linkTargets from 'options/linkTargets'
import { Emitter } from 'helpers/events'
import { LIST_MODAL_CLEAR_SELECTION } from 'options/events'
import Select, { Option } from 'elements/Select'
import Icon from 'elements/Icon'
import ListItems from 'containers/Lists/ChildListView/Items'
import Drawer from 'elements/Drawer'
import Modal from 'elements/Modal'
import SelectList from 'containers/Lists/Select'
import { Row, Col } from 'elements/Grid'

const FormView = linkTargets.listRecord.formComponent

const Container = styled(Row)`
  .ant-select {
    min-width: 200px;
  }

  .ant-select-sm {
    .ant-select-selection--single {
      height: 26px;
      line-height: 24px;
    }
  }

  .ant-col {
    margin-bottom: 12px;

    .ant-btn {
      padding: 5px;
      line-height: 5px;
      height: 26px;
    }
  }
`

function Component(props) {
  const { entityType, entityId, getItems, createItems, deleteItems, ...rest } = props

  const [state, updateState] = useImmer({})

  function setState(path, value) {
    updateState((draft) => {
      set(draft, path, value)
    })
  }

  const entityListTypes = uniqBy(
    (state?.items ?? []).map((each) => ({
      type: each.listType,
      typeName: each.listTypeDescription,
    })),
    'type'
  )

  const filteredItems = (state?.items ?? []).filter((each) => each.listType === state.selectedListType)
  const selectedItem = (state?.items ?? []).find((one) => one.entityListId === state.selectedEntityListId)

  async function fetchItems() {
    let items = []

    try {
      setState('loading', true)

      const response = await getItems({ entityType, entityId })

      items = orderBy(response.value.data.items, ['listType', 'listName'], ['desc', 'asc'])

      setState('items', items)

      if (isEmpty(items)) {
        updateState((draft) => {
          draft.selectedListType = null
          draft.selectedEntityListId = null
        })
      } else {
        updateState((draft) => {
          draft.selectedListType = items[0].listType
          draft.selectedEntityListId = items[0].entityListId
        })
      }
    } catch (error) {
      showError({ error })
    } finally {
      setState('loading', false)
    }

    return items
  }

  function handleDeleteClick() {
    Modal.confirm({
      autoFocusButton: 'ok',
      title: t('confirmRemoveListAssociation'),
      okType: 'danger',
      okText: t('remove'),
      cancelText: t('cancel'),
      onOk: async () => {
        try {
          const response = await deleteItems([state.selectedEntityListId])

          showClientNotifications({ response })

          if (response.value.data.failureCount > 0) {
            throw new Error()
          }

          const updatedItems = await fetchItems()
          const nextItem =
            updatedItems.find((one) => one.listType === state.selectedListType) || updatedItems[0]

          updateState((draft) => {
            draft.selectedEntityListId = nextItem?.entityListId ?? null
            draft.selectedListType = nextItem?.listType ?? null
          })
        } catch (error) {
          showError({ error })
        }
      },
    })
  }

  async function handleAddListClick() {
    const creating = (state?.addListRecords ?? []).map((each) => ({
      entityType,
      entityId,
      listId: each.id,
      listType: each.type,
    }))

    try {
      setState('addListLoading', false)

      const response = await createItems(creating)

      showClientNotifications({ response })

      if (response.value.data.items[0]) {
        await fetchItems()

        setState('addListVisible', false)

        Emitter.emit(LIST_MODAL_CLEAR_SELECTION)
      }
    } catch (error) {
      showError({ error })
    } finally {
      setState('addListLoading', false)
    }
  }

  React.useEffect(() => {
    fetchItems()
  }, [entityType, entityId])

  return (
    <>
      <Container type="flex" justify="end" gutter={6}>
        <Col>
          <Select
            value={state.selectedListType}
            onChange={(value) => {
              updateState((draft) => {
                draft.selectedListType = value
                draft.selectedEntityListId = (state?.items ?? []).find(
                  (one) => one.listType === value
                )?.entityListId
              })
            }}
            size="small"
            allowClear={false}
            allowSearch={false}
            disabled={isEmpty(entityListTypes)}
          >
            {entityListTypes.map((each) => (
              <Option key={each.type} value={each.type}>
                {each.typeName}
              </Option>
            ))}
          </Select>
        </Col>
        <Col>
          <Select
            value={state.selectedEntityListId}
            onChange={(value) => setState('selectedEntityListId', value)}
            size="small"
            allowClear={false}
            allowSearch={false}
            disabled={isEmpty(filteredItems)}
          >
            {filteredItems.map((each) => (
              <Option key={each.entityListId} value={each.entityListId}>
                {each.listName}
              </Option>
            ))}
          </Select>
        </Col>
        <Col>
          <Tooltip title={t('editList')}>
            <Button
              size="small"
              onClick={() =>
                updateState((draft) => {
                  draft.editDrawerVisible = true
                  draft.editDrawerSaving = false
                })
              }
              disabled={isNil(state.selectedEntityListId)}
            >
              <Icon type="Edit" />
            </Button>
          </Tooltip>
        </Col>
        <Col>
          <Tooltip title={t(`removeListFrom${entityType}`)} placement="topLeft">
            <Button size="small" onClick={handleDeleteClick} disabled={isNil(state.selectedEntityListId)}>
              <Icon type="Delete" />
            </Button>
          </Tooltip>
        </Col>
        <Col>
          <Tooltip title={t(`addListTo${entityType}`)} placement="topLeft">
            <Button
              size="small"
              type="primary"
              onClick={() =>
                updateState((draft) => {
                  draft.addListVisible = true
                  draft.addListRecords = null
                })
              }
            >
              <Icon type="Add" bold />
            </Button>
          </Tooltip>
        </Col>
      </Container>
      {state.loading ? (
        <Spin />
      ) : !isNil(selectedItem?.listId) ? (
        <ListItems listId={selectedItem?.listId} listType={selectedItem?.listType} {...rest} />
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '240px' }}>
          <Empty description={t('addListToEntity')} />
        </div>
      )}
      <Drawer
        title={t('editList')}
        size={linkTargets.listRecord.formSize}
        visible={state.editDrawerVisible}
        onClose={() => setState('editDrawerVisible', false)}
        saving={state.editDrawerSaving}
      >
        <FormView
          linkTargetRecord={{ listId: selectedItem?.listId }}
          onSave={async (pending) => {
            setState('editDrawerSaving', pending)

            if (!pending) {
              await fetchItems()
            }
          }}
          onSaveAndClose={() => setState('editDrawerVisible', false)}
          onCancel={() => setState('editDrawerVisible', false)}
        />
      </Drawer>
      <Modal
        title={t(`addListTo${entityType}`)}
        visible={state.addListVisible}
        onCancel={() => setState('addListVisible', false)}
        footer={
          <>
            <Button onClick={() => setState('addListVisible', false)}>{t('close')}</Button>
            <Button
              onClick={handleAddListClick}
              loading={state.addListLoading}
              type="primary"
              disabled={isNil(state.addListRecords)}
            >
              {t('addList')}
            </Button>
          </>
        }
        width={992}
      >
        <SelectList
          filterProps={{ entityType, entityId }}
          onSelect={(values) => setState('addListRecords', values)}
          mode="multiple"
        />
      </Modal>
    </>
  )
}

export default Component
